/**
 * ResponseDispatcher — squelette de routage d'une réponse JSON `{todo, msg, …}` (ha/448).
 *
 * Factorise la duplication entre `Anchor.processForm`/`processAction` et
 * `FormController.processBtnClick` : tous logguaient `msg`, switchaient sur `todo`,
 * et tombaient en `processAction(response)` par défaut. Le routage est constant ;
 * les branches sont site-spécifiques (ex. arg de `reloadApp`, hooks UI state) →
 * chaque site fournit son `handlers` map.
 *
 * Écart documenté entre les 4 anciens switchs (ha/448) :
 *  - Anchor.processForm  : reloadArg = `{id: response.id}`, pas d'extras.
 *  - Anchor.processAction: reloadArg = `response.d`, extras url/dom/bulk/steps.
 *  - FormController      : reloadArg = `response` entier, branches setState UI,
 *                          fallback noTodo divergent (`has("app")` → showApp).
 *  - msg-log : Anchor logge avant la switch ; FormController logge UNIQUEMENT
 *    dans les branches success/error.
 *
 * Le squelette préserve ces différences via `handlers`, `noTodo`, `skipMsgLog`.
 * Aucun changement de comportement observable côté appelants.
 */

import { log, getAppService } from "../../../HA";

export type TodoHandler = (response: Map<any, any>) => void;

export interface ResponseDispatchOptions {
    /** Per-todo branches. `default` = fallback for unknown todo values. */
    handlers: Partial<Record<string, TodoHandler>> & { default?: TodoHandler };
    /** Branch when `response.get("todo")` is absent. Defaults to `handlers.default`. */
    noTodo?: TodoHandler;
    /** Set true to skip the automatic `log(response.msg)` (FormController logge case-by-case). */
    skipMsgLog?: boolean;
}

const FALLBACK: TodoHandler = (r) => getAppService().processAction(r);

/**
 * Adopte un token CSRF frais porté par la réponse (`csrf_token`) dans la meta
 * `csrf-token` du document. Le serveur rotate le token à la création de session
 * (login, MFA verify) : sans adoption, toute requête mutante suivante (dont le
 * step `app` post-login) partirait avec l'ancien token → 403.
 */
export function adoptCsrfToken(response: Map<any, any>): void {
    const token = response.get("csrf_token");
    if (typeof token !== "string" || token === "" || typeof document === "undefined") {
        return;
    }
    let meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    if (!meta) {
        meta = document.createElement("meta");
        meta.name = "csrf-token";
        document.head.appendChild(meta);
    }
    meta.content = token;
}

export function dispatchResponse(response: Map<any, any>, opts: ResponseDispatchOptions): void {
    adoptCsrfToken(response);
    if (!opts.skipMsgLog && response.get("msg")) {
        log(response.get("msg"));
    }
    const todo = response.get("todo");
    const defaultHandler = opts.handlers.default ?? FALLBACK;
    if (todo) {
        const handler = opts.handlers[todo] ?? defaultHandler;
        handler(response);
        return;
    }
    (opts.noTodo ?? defaultHandler)(response);
}
